import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { formatDate, formatInteger } from '../../../lib/format';

/**
 * The agreements customers accept (owner's decision, 2 October 2026): today
 * the data processing agreement. The wording is the website's legal page; this
 * page records that it changed. Publishing a version asks every organisation
 * to accept again before they can use the console.
 */
export default function ContentAgreementsPage() {
  const t = useT();
  const q = useQuery({ queryKey: ['admin', 'agreements'], queryFn: () => admin.listAgreements() });
  const rows = q.data?.data ?? [];
  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.section.content')} title={t('content.agreements.title')} description={t('content.agreements.description')} />
      {rows.map((a) => <AgreementCard key={a.kind} agreement={a} />)}
    </Stack>
  );
}

function AgreementCard({ agreement }: { agreement: admin.AdminAgreement }) {
  const t = useT();
  const qc = useQueryClient();
  const [version, setVersion] = useState('');
  const [summary, setSummary] = useState('');
  const [versionError, setVersionError] = useState<string | undefined>();
  const current = agreement.versions[0];

  const publish = useMutation({
    mutationFn: () => admin.publishAgreementVersion(agreement.kind, { version: version.trim(), summary: summary.trim() || undefined }),
    onMutate: () => setVersionError(undefined),
    onSuccess: () => {
      setVersion(''); setSummary('');
      void qc.invalidateQueries({ queryKey: ['admin', 'agreements'] });
      notifySuccess(t('content.agreements.published'), agreement.name);
    },
    onError: (e) => {
      if (isApiError(e) && (e.code === 'VERSION_EXISTS' || e.code === 'VERSION_REQUIRED')) { setVersionError(e.message); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), agreement.name);
    }
  });

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>{agreement.name}</Title>
          {current && <Badge variant="light">{t('content.agreements.current', { version: current.version })}</Badge>}
        </Group>
        {current && (
          <Text fz="sm">
            {t('content.agreements.acceptedCount', {
              accepted: formatInteger(current.accepted_by_organisations),
              total: formatInteger(agreement.active_organisations)
            })}
          </Text>
        )}
        <Stack gap={4}>
          {agreement.versions.map((v) => (
            <Text key={v.version} fz="sm" c="var(--rr-color-text-muted)">
              {t('content.agreements.versionLine', {
                version: v.version, date: formatDate(v.published_at), count: formatInteger(v.accepted_by_organisations)
              })}
              {v.summary ? `: ${v.summary}` : ''}
            </Text>
          ))}
        </Stack>
        <Alert color="yellow" variant="light">{t('content.agreements.publishWarning')}</Alert>
        <TextInput label={t('content.agreements.version')} description={t('content.agreements.versionHint', { path: agreement.path })}
          value={version} onChange={(e) => setVersion(e.currentTarget.value)} error={versionError} placeholder="2026-10-02" maxLength={40} />
        <Textarea label={t('content.agreements.summary')} description={t('content.agreements.summaryHint')}
          value={summary} onChange={(e) => setSummary(e.currentTarget.value)} minRows={2} maxLength={1000} />
        <Group justify="flex-end">
          <Button onClick={() => publish.mutate()} loading={publish.isPending} disabled={version.trim().length < 4}>
            {t('content.agreements.publish')}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
